import React from 'react';
import {
  ApAuthenticationContextProvider,
  ApAuthenticationContextConsumer,
  Telemetry,
} from '@alixpartners/ui-utils';
import { createBrowserHistory } from 'history';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import './styles/fonts.scss';
import GlobalStyles from './globalStyles';

import { ErrorPage } from './pages/Error';
import { HandleSignin } from './components/HandleSignin';

import { DataManagerContainer } from './pages/DataManagerContainer';
import { Dashboard } from './pages/DataManagerContainer/Dashboard';
import { UploadMapping } from './pages/DataManagerContainer/UploadMapping';
import { CompletedMappings } from './pages/DataManagerContainer/CompletedMappings';
import { Reports } from './pages/DataManagerContainer/Reports';
import { SettingsContainer } from './pages/SettingsContainer/SettingsContainer';
import { EnvironmentSetup } from './pages/SettingsContainer/EnvironmentSetup';
import { BackEndAccess } from './pages/SettingsContainer/BackEndAccess';
import { ReportManagerContainer } from './pages/ReportManagerContainer/ReportManagerContainer';
import { BuildReports } from './pages/ReportManagerContainer/BuildReports';
import { ShareReports } from './pages/ReportManagerContainer/ShareReports';
import { ClientReportContainer } from './pages/ClientReportContainer';
import { ClientReport } from './pages/ClientReportContainer/ClientReport';
import { Publishing } from './pages/DataManagerContainer/Publishing';
import { inIframe } from './utils/utils';

const history = createBrowserHistory();
Telemetry.setup(process.env.REACT_APP_AI_KEY, history);

if (window.location.pathname !== '/' && !sessionStorage.getItem('redirect')) {
  sessionStorage.setItem('redirect', window.location.pathname);
}

const App = (): JSX.Element | null => {
  if (inIframe()) {
    console.info('DEBUG: cancelling iframe render');

    return null;
  }

  return (
    <>
      <GlobalStyles />
      {/* @ts-expect-error */}
      <ApAuthenticationContextProvider
        errorComponent={({ errorType, errorDescription, onClick }) => (
          <ErrorPage
            title={errorType}
            description={errorDescription}
            onClick={onClick}
          />
        )}
      >
        <BrowserRouter>
          <ApAuthenticationContextConsumer>
            {({ user }) => {
              const owner = (user?.profile.name as string)
                .split(', ')
                .join(' ');

              return (
                <Routes>
                  <Route path="/signin" element={<HandleSignin />} />
                  <Route
                    path="/data-manager"
                    element={<DataManagerContainer />}
                  >
                    <Route
                      index
                      element={<Navigate to="dashboard" replace />}
                    />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="upload-mapping">
                      <Route
                        index
                        element={<Navigate to="accounts-payable" replace />}
                      />
                      <Route
                        path=":section"
                        element={<UploadMapping userName={user!.userName} />}
                      />
                    </Route>
                    <Route
                      path="completed-mappings"
                      element={<CompletedMappings />}
                    />
                    <Route path="reports" element={<Reports />} />
                    <Route path="publishing" element={<Publishing />} />
                    <Route
                      path="*"
                      element={<Navigate to="dashboard" replace />}
                    />
                  </Route>
                  <Route path="/settings" element={<SettingsContainer />}>
                    <Route
                      index
                      element={<Navigate to="environment-setup" replace />}
                    />
                    <Route
                      path="*"
                      element={<Navigate to="environment-setup" replace />}
                    />
                    <Route
                      path="environment-setup"
                      element={<EnvironmentSetup />}
                    />
                    <Route path="back-end-access" element={<BackEndAccess />} />
                  </Route>
                  <Route
                    path="/report-manager"
                    element={<ReportManagerContainer />}
                  >
                    <Route
                      index
                      element={<Navigate to="build-reports" replace />}
                    />
                    <Route
                      path="build-reports"
                      element={<BuildReports userName={owner} />}
                    />
                    <Route
                      path="all-reports"
                      element={
                        <ShareReports
                          userName={owner}
                          userRole={user?.profile?.roles?.[0] || ''}
                        />
                      }
                    />
                    <Route
                      path="shared-reports"
                      element={
                        <ShareReports
                          userName={owner}
                          userRole={user?.profile?.roles?.[0] || ''}
                          isSharedReportstTab
                        />
                      }
                    />
                  </Route>
                  <Route
                    path="*"
                    element={<Navigate to="/data-manager" replace />}
                  />
                  <Route
                    path="client-report-site"
                    element={<ClientReportContainer />}
                  >
                    <Route index element={<ClientReport />} />
                    <Route index path=":reportId" element={<ClientReport />} />
                    <Route
                      index
                      path=":reportId/:pageId"
                      element={<ClientReport />}
                    />
                  </Route>
                </Routes>
              );
            }}
          </ApAuthenticationContextConsumer>
        </BrowserRouter>
      </ApAuthenticationContextProvider>
    </>
  );
};

export default App;
